import api from './api';

// User types based on API response (sys_user table, types 1 and 2)
export interface User {
  id: string;
  username: string;
  userRealName?: string;
  phone?: string;
  email?: string;
  type: number; // 1=SuperAdmin, 2=TenantAdmin
  status?: number;
  tenantId?: string;
  createTime?: string;
  updateTime?: string;
  createBy?: string;
  updateBy?: string;
}

export interface UserListParams {
  search?: string;
  tenantId?: string;
  type?: number;
  page?: number;
  pageSize?: number;
}

export interface UserCreateData {
  username: string;
  password: string;
  userRealName?: string;
  phone?: string;
  email?: string;
  type: number;
  tenantId?: string;
}

export interface UserUpdateData {
  userRealName?: string;
  phone?: string;
  email?: string;
  status?: number;
}

export interface UserResetPasswordData {
  password: string;
  temporary?: boolean;
}

// Per-user brute-force state returned by /users/:id/lockout-status
export interface UserLockoutStatus {
  disabled: boolean;
  numFailures: number;
  lastFailure: number;
  lastIPFailure: string | null;
  numTemporaryLockouts: number;
}

// Provisioning types
export interface ProvisionUsersRequest {
  emails: string[];
  userType: number;
  tenantId: string;
  // Owning channel for a ChannelAdmin (userType 5) invite. The API creates the
  // channel membership up front so the admin is scoped on acceptance; ignored
  // for every other user type.
  channelId?: string;
  roleId?: string;
  emailTemplateId?: string;
  expiresInDays?: number;
}

export interface ProvisionResult {
  email: string;
  status: 'invited' | 'already_exists' | 'invalid_email' | 'error';
  invitationId?: string;
  error?: string;
}

export interface ProvisionResponse {
  provisioned: number;
  failed: number;
  results: ProvisionResult[];
}

export interface Invitation {
  id: string;
  email: string;
  userType: number;
  tenantId: string | null;
  tenantName: string | null;
  roleId: string | null;
  roleName: string | null;
  invitedBy: string;
  inviterName: string | null;
  status: 'pending' | 'sent' | 'accepted' | 'expired' | 'revoked';
  sentAt: string | null;
  acceptedAt: string | null;
  expiresAt: string;
  createdAt: string;
}

export interface InvitationListParams {
  status?: string;
  tenantId?: string;
}

export interface PaginatedUserResponse {
  items: User[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

// Session types
export interface SessionRecord {
  id: string;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  expiresAt: string;
  lastActivity: string;
  revoked: number;
  revokedAt: string | null;
  revokedReason: string | null;
  status: 'Active' | 'Expired' | 'Revoked';
}

// Role assignment types
export interface RoleAssignment {
  id: string;
  roleId: string;
  roleName: string;
  roleType: string;
  permissions: string[];
  description: string | null;
  assignedAt: string;
  assignedBy: string;
}

const userService = {
  /**
   * List all users (auto-filtered by tenant)
   */
  list: async (params?: UserListParams) => {
    const response = await api.get('/users', { params });
    return response.data as PaginatedUserResponse;
  },

  /**
   * Get current user profile
   */
  getCurrentUser: async () => {
    const response = await api.get('/users/me');
    return response.data as User;
  },

  /**
   * Get user by ID (validates ownership)
   */
  getById: async (id: string) => {
    const response = await api.get(`/users/${id}`);
    return response.data as User;
  },

  /**
   * Create new user (assigns tenant)
   */
  create: async (data: UserCreateData) => {
    const response = await api.post('/users', data);
    return response.data as User;
  },

  /**
   * Update user (validates ownership)
   */
  update: async (id: string, data: UserUpdateData) => {
    const response = await api.put(`/users/${id}`, data);
    return response.data as User;
  },

  /**
   * Delete user (validates ownership)
   */
  delete: async (id: string) => {
    await api.delete(`/users/${id}`);
  },

  /**
   * Admin action: set a user's password directly via the KC Admin API.
   * Pass `temporary: true` to force the user to change it on next login.
   */
  resetPassword: async (id: string, data: UserResetPasswordData) => {
    const response = await api.post(`/users/${id}/reset-password`, data);
    return response.data;
  },

  /**
   * Admin action: trigger KC to send a password-reset email to the user.
   */
  sendPasswordResetEmail: async (id: string) => {
    const response = await api.post(`/users/${id}/send-reset-password`);
    return response.data;
  },

  /**
   * Get the user's brute-force lockout status.
   */
  getLockoutStatus: async (id: string): Promise<UserLockoutStatus> => {
    const response = await api.get(`/users/${id}/lockout-status`);
    return response.data as UserLockoutStatus;
  },

  /**
   * Clear the user's brute-force lockout (admin unlock).
   */
  unlock: async (id: string) => {
    const response = await api.post(`/users/${id}/unlock`);
    return response.data;
  },

  /**
   * Sync users from Keycloak
   */
  syncFromKeycloak: async () => {
    const response = await api.post('/users/sync-keycloak');
    return response.data;
  },

  // ==========================================
  // Provisioning / Invitation Methods
  // ==========================================

  /**
   * Provision multiple users (bulk invite)
   */
  provisionUsers: async (data: ProvisionUsersRequest) => {
    const response = await api.post('/users/provision', data);
    return response.data as ProvisionResponse;
  },

  /**
   * List invitations
   */
  listInvitations: async (params?: InvitationListParams) => {
    const response = await api.get('/users/invitations', { params });
    return response.data as { invitations: Invitation[] };
  },

  /**
   * Resend an invitation
   */
  resendInvitation: async (invitationId: string) => {
    const response = await api.post(`/users/invitations/${invitationId}/resend`);
    return response.data;
  },

  /**
   * Revoke an invitation
   */
  revokeInvitation: async (invitationId: string) => {
    await api.delete(`/users/invitations/${invitationId}`);
  },

  // ==========================================
  // Role Assignment Methods
  // ==========================================

  /**
   * Get roles assigned to a user
   */
  getUserRoles: async (userId: string) => {
    const response = await api.get(`/users/${userId}/roles`);
    return response.data as RoleAssignment[];
  },

  /**
   * Assign a role to a user
   */
  assignRole: async (userId: string, roleId: string) => {
    const response = await api.post(`/users/${userId}/roles`, { roleId });
    return response.data;
  },

  /**
   * Unassign a role from a user
   */
  unassignRole: async (userId: string, roleId: string) => {
    await api.delete(`/users/${userId}/roles/${roleId}`);
  },
  // ==========================================
  // Session Management Methods
  // ==========================================

  /**
   * Get sessions for a user
   */
  getUserSessions: async (userId: string, params?: { page?: number; pageSize?: number; search?: string }) => {
    const response = await api.get(`/users/${userId}/sessions`, { params });
    return response.data as {
      items: SessionRecord[];
      pagination: { page: number; pageSize: number; total: number; totalPages: number };
    };
  },

  /**
   * Revoke a specific session
   */
  revokeSession: async (userId: string, sessionId: string) => {
    await api.delete(`/users/${userId}/sessions/${sessionId}`);
  },

  /**
   * Revoke all active sessions for a user
   */
  revokeAllSessions: async (userId: string) => {
    const response = await api.post(`/users/${userId}/sessions/revoke-all`);
    return response.data as { revoked: number };
  },

  /**
   * Export sessions as CSV
   */
  exportSessionsCSV: async (userId: string) => {
    const response = await api.get(`/users/${userId}/sessions/export`, {
      responseType: 'blob',
    } as any);
    return response as any;
  },
};

export default userService;
